//src/components/pendent-load/PendentLoadRoutePanel.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  GripVertical,
  Info,
  Pencil,
  Plus,
  X,
} from "lucide-react";

const NORMAL_POINT_LIMITS = {
  length:
    13.6,

  width:
    2.5,

  height:
    2.7,
};

const SPECIAL_POINT_LIMITS = {
  length:
    20,

  width:
    4,

  height:
    5,
};

function safeDocumentBody() {
  return typeof document !==
    "undefined"
    ? document.body
    : null;
}

function resolvePlaceSelectValue(
  point,
  options
) {
  if (
    point?.placeId !==
      null &&
    point?.placeId !==
      undefined
  ) {
    const byId =
      options.find(
        (
          option
        ) =>
          String(
            option.value
          ) ===
          String(
            point.placeId
          )
      );

    if (
      byId
    ) {
      return byId;
    }
  }

  if (
    point?.location
  ) {
    const byLabel =
      options.find(
        (
          option
        ) =>
          option.label ===
          point.location
      );

    if (
      byLabel
    ) {
      return byLabel;
    }

    return {
      value:
        point.location,

      label:
        point.location,

      metaCity:
        point.city ||
        "",

      metaCountry:
        point.country ||
        "",

      fullData:
        null,
    };
  }

  return null;
}

function renderPlaceOption(
  option,
  {
    context,
  }
) {
  const city =
    option?.metaCity ||
    option?.fullData?.city ||
    "";

  const country =
    option?.metaCountry ||
    option?.fullData?.country ||
    "";

  const meta =
    [
      city,
      country,
    ]
      .filter(
        Boolean
      )
      .join(
        ", "
      );

  return (
    <div className="current-load-place-option">
      <strong>
        {option.label}
      </strong>

      {meta && (
        <span
          className={
            context ===
            "value"
              ? "is-value"
              : ""
          }
        >
          {meta}
        </span>
      )}
    </div>
  );
}

function makeEmptyNewStop(
  type
) {
  return {
    id:
      null,

    sequence:
      null,

    type,

    location:
      "",

    date:
      "",

    hour:
      "",

    length:
      "",

    width:
      "",

    height:
      "",

    placeId:
      null,

    address:
      "",

    postalCode:
      "",

    city:
      "",

    country:
      "",

    gMapsLink:
      "",

    coordinates:
      "",

    latitude:
      null,

    longitude:
      null,

    contactPhone:
      "",

    workingHours:
      null,

    reference:
      "",

    loadingAccess:
      null,

    handlingEquipment:
      null,

    handlingEquipmentOther:
      "",

    showMoreFields:
      false,

    locationError:
      false,

    dateError:
      false,

    hourError:
      false,

    lengthError:
      "",

    widthError:
      "",

    heightError:
      "",

    handlingEquipmentOtherError:
      false,
  };
}

function getStopKey(
  stop,
  index
) {
  return String(
    stop?.__workspaceKey ||
      stop?.id ||
      `pendent-load-stop-${index}`
  );
}

function isPickupStop(
  stop
) {
  return (
    String(
      stop?.type ||
        stop?.__sourceType ||
        ""
    ).toUpperCase() ===
    "PICKUP"
  );
}

function getStopTitle(
  stop,
  fallback
) {
  return (
    stop?.placeName ||
    stop?.location ||
    fallback
  );
}

function getStopLocation(
  stop,
  fallback
) {
  const cityCountry = [
    stop?.city,
    stop?.country,
  ]
    .map(
      (
        value
      ) =>
        String(
          value ||
            ""
        ).trim()
    )
    .filter(
      Boolean
    )
    .join(
      ", "
    );

  return (
    cityCountry ||
    stop?.address ||
    fallback
  );
}

function formatDate(
  value,
  locale,
  fallback
) {
  const rawValue =
    String(
      value ||
        ""
    ).trim();

  if (
    !rawValue
  ) {
    return fallback;
  }

  const match =
    rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (
    !match
  ) {
    return rawValue;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",

      month:
        "long",

      year:
        "numeric",
    }
  ).format(
    new Date(
      Number(
        match[1]
      ),

      Number(
        match[2]
      ) -
        1,

      Number(
        match[3]
      )
    )
  );
}

function getStopDateLabel(
  stop,
  locale,
  t
) {
  const date =
    formatDate(
      stop?.date,
      locale,
      t(
        "routePanel.dateUndefined"
      )
    );

  const hour =
    String(
      stop?.hour ||
        ""
    ).trim();

  if (
    hour
  ) {
    return `${date} ${hour}`;
  }

  return date;
}

function normalizeDisplayValue(
  value
) {
  if (
    value ===
      null ||
    value ===
      undefined ||
    String(
      value
    ).trim() ===
      ""
  ) {
    return "";
  }

  if (
    typeof value ===
    "object"
  ) {
    if (
      Array.isArray(
        value
      )
    ) {
      return value.join(
        ", "
      );
    }

    if (
      value.lat !==
        undefined &&
      value.lng !==
        undefined
    ) {
      return `${value.lat}, ${value.lng}`;
    }

    if (
      value.latitude !==
        undefined &&
      value.longitude !==
        undefined
    ) {
      return `${value.latitude}, ${value.longitude}`;
    }

    try {
      return JSON.stringify(
        value
      );
    } catch {
      return "";
    }
  }

  return String(
    value
  );
}

function getDisplayValue(
  value,
  suffix = "",
  undefinedLabel
) {
  const normalizedValue =
    normalizeDisplayValue(
      value
    );

  if (
    !normalizedValue
  ) {
    return undefinedLabel;
  }

  return `${normalizedValue}${suffix}`;
}

const LOADING_ACCESS_I18N_KEYS = {
  SIDE:
    "side",
  REAR:
    "rear",
  TOP:
    "top",
  DOCK:
    "dock",
  ANY:
    "any",
};

const HANDLING_EQUIPMENT_I18N_KEYS = {
  FORKLIFT:
    "forklift",
  PALLET_JACK:
    "palletJack",
  ELECTRIC_PALLET_JACK:
    "electricPalletJack",
  CRANE:
    "crane",
  LOADING_RAMP:
    "loadingRamp",
  TAIL_LIFT:
    "tailLift",
  HAND_LOAD:
    "handLoad",
  CONVEYOR:
    "conveyor",
  OTHER:
    "other",
};

function getTranslatedEnumLabel(
  value,
  keyMap,
  prefix,
  t
) {
  const normalized =
    String(
      value ||
        ""
    )
      .trim()
      .toUpperCase();

  if (
    !normalized
  ) {
    return "";
  }

  const key =
    keyMap[
      normalized
    ];

  return key
    ? t(
        `${prefix}.${key}`
      )
    : normalized;
}

function StopDetailItem({
  label,
  field,
  value,
  suffix = "",
  type = "text",
  inputMode,
  step,
  isEditing,
  onChange,
  displayValue,
  undefinedLabel,
}) {
  return (
    <div className="edit-request-stop-detail-item">
      <span>
        {label}
      </span>

      {isEditing ? (
        <input
          type={
            type
          }
          step={
            step
          }
          inputMode={
            inputMode
          }
          value={
            normalizeDisplayValue(
              value
            )
          }
          placeholder={
            undefinedLabel
          }
          onChange={(event) =>
            onChange(
              field,
              event.target.value
            )
          }
        />
      ) : (
        <strong>
          {getDisplayValue(
            displayValue ??
              value,
            suffix,
            undefinedLabel
          )}
        </strong>
      )}
    </div>
  );
}

export default function PendentLoadRoutePanel({
  stops = [],
  readOnly = false,
  onUpdateStop,
  onSaveStop,
  onRemoveStop,
  onAddPickup,
  onAddDelivery,
  canAddPickup = true,
  canAddDelivery = true,
  placeOptions = [],
  loadingAccessOptions = [],
  handlingEquipmentOptions = [],
  isSpecialLoad = false,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_pendentload"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const [
    expandedStopKey,
    setExpandedStopKey,
  ] =
    useState(
      ""
    );

  const [
    editingStopKey,
    setEditingStopKey,
  ] =
    useState(
      ""
    );

  const [
    draft,
    setDraft,
  ] =
    useState(
      null
    );

    const [
      addMenuOpen,
      setAddMenuOpen,
    ] =
      useState(
        false
      );
  
    const [
      newStopType,
      setNewStopType,
    ] =
      useState(
        ""
      );
  
    const [
      newStopDraft,
      setNewStopDraft,
    ] =
      useState(
        null
      );
  
    const newStopDateInputRef =
      useRef(
        null
      );
  
    useEffect(
      () => {
        if (
          !newStopDraft
        ) {
          return undefined;
        }
  
        const handleKeyDown =
          (
            event
          ) => {
            if (
              event.key ===
              "Escape"
            ) {
              setNewStopDraft(
                null
              );
  
              setNewStopType(
                ""
              );
            }
          };
  
        document.body.classList.add(
          "current-load-new-stop-modal-open"
        );
  
        window.addEventListener(
          "keydown",
          handleKeyDown
        );
  
        return () => {
          document.body.classList.remove(
            "current-load-new-stop-modal-open"
          );
  
          window.removeEventListener(
            "keydown",
            handleKeyDown
          );
        };
      },
      [
        newStopDraft,
      ]
    );
  
    const routeStops =
    useMemo(
      () =>
        (
          Array.isArray(
            stops
          )
            ? stops
            : []
        ).map(
          (
            stop,
            index
          ) => ({
            ...stop,

            __renderKey:
              getStopKey(
                stop,
                index
              ),

            __isPickup:
              isPickupStop(
                stop
              ),

            __stopNumber:
              index +
              1,
          })
        ),
      [
        stops,
      ]
    );

  const startEditing =
    (
      stop
    ) => {
      if (
        readOnly
      ) {
        return;
      }

      setExpandedStopKey(
        stop.__renderKey
      );

      setEditingStopKey(
        stop.__renderKey
      );

      setDraft({
        ...stop,
      });
    };

  const cancelEditing =
    () => {
      setEditingStopKey(
        ""
      );

      setDraft(
        null
      );
    };

  const updateDraft =
    (
      field,
      value
    ) => {
      setDraft(
        (
          currentDraft
        ) => ({
          ...currentDraft,

          [
            field
          ]:
            value,
        })
      );
    };

  const saveStop =
    async (
      stop
    ) => {
      if (
        readOnly ||
        !draft
      ) {
        return;
      }

      if (
        onSaveStop
      ) {
        const saved =
          await onSaveStop(
            stop,
            draft
          );

        if (
          saved ===
          false
        ) {
          return;
        }
      } else {
        onUpdateStop?.(
          stop,
          draft
        );
      }

      cancelEditing();
    };

  const removeStop =
    async (
      stop
    ) => {
      if (
        readOnly
      ) {
        return;
      }

      const removed =
        await onRemoveStop?.(
          stop
        );

      if (
        removed ===
        false
      ) {
        return;
      }

      if (
        expandedStopKey ===
        stop.__renderKey
      ) {
        setExpandedStopKey(
          ""
        );
      }

      if (
        editingStopKey ===
        stop.__renderKey
      ) {
        cancelEditing();
      }
    };

  const closeNewStopModal =
    () => {
      setNewStopDraft(
        null
      );

      setNewStopType(
        ""
      );
    };

  const openNewStopModal =
    (
      type
    ) => {
      if (
        type ===
          "PICKUP" &&
        !canAddPickup
      ) {
        return;
      }

      if (
        type ===
          "DELIVERY" &&
        !canAddDelivery
      ) {
        return;
      }

      setAddMenuOpen(
        false
      );

      setNewStopType(
        type
      );

      setNewStopDraft(
        makeEmptyNewStop(
          type
        )
      );
    };

  const patchNewStop =
    (
      patch
    ) => {
      setNewStopDraft(
        (
          currentDraft
        ) =>
          currentDraft
            ? {
                ...currentDraft,
                ...patch,
              }
            : currentDraft
      );
    };

  const handleNewStopPlaceChange =
    (
      selectedOption
    ) => {
      if (
        !selectedOption
      ) {
        patchNewStop({
          placeId:
            null,

          location:
            "",

          address:
            "",

          postalCode:
            "",

          city:
            "",

          country:
            "",

          gMapsLink:
            "",

          coordinates:
            "",

          latitude:
            null,

          longitude:
            null,

          contactPhone:
            "",

          workingHours:
            null,

          locationError:
            false,
        });

        return;
      }

      if (
        selectedOption.__isNew__
      ) {
        patchNewStop({
          placeId:
            null,

          location:
            selectedOption.label ||
            selectedOption.value ||
            "",

          address:
            "",

          postalCode:
            "",

          city:
            "",

          country:
            "",

          gMapsLink:
            "",

          coordinates:
            "",

          latitude:
            null,

          longitude:
            null,

          contactPhone:
            "",

          workingHours:
            null,

          locationError:
            false,
        });

        return;
      }

      const place =
        selectedOption.fullData;

      if (
        place
      ) {
        patchNewStop({
          placeId:
            place.id ??
            selectedOption.value ??
            null,

          location:
            place.placeName ||
            place.name ||
            selectedOption.label ||
            "",

          address:
            place.address ||
            "",

          postalCode:
            place.postalCode ||
            "",

          city:
            place.city ||
            "",

          country:
            place.country ||
            "",

          gMapsLink:
            place.gMapsLink ||
            "",

          coordinates:
            place.coordinates ||
            "",

          latitude:
            place.latitude ??
            place.lat ??
            null,

          longitude:
            place.longitude ??
            place.lng ??
            place.lon ??
            null,

          contactPhone:
            place.contactPhone ||
            "",

          workingHours:
            place.workingHours ??
            null,

          locationError:
            false,
        });

        return;
      }

      patchNewStop({
        placeId:
          selectedOption.value ??
          null,

        location:
          selectedOption.label ||
          "",

        locationError:
          false,
      });
    };

  const confirmNewStop =
    () => {
      if (
        !newStopDraft ||
        !newStopType
      ) {
        return;
      }

      const dimensionLimits =
        isSpecialLoad
          ? SPECIAL_POINT_LIMITS
          : NORMAL_POINT_LIMITS;

      const getDimensionError =
        (
          field,
          value
        ) => {
          const cleanValue =
            String(
              value ??
              ""
            ).trim();

          if (
            !cleanValue
          ) {
            return "";
          }

          const parsedValue =
            Number(
              cleanValue.replace(
                ",",
                "."
              )
            );

          if (
            !Number.isFinite(
              parsedValue
            ) ||
            parsedValue <=
              0
          ) {
            return t(
              "errors.positiveNumber"
            );
          }

          if (
            parsedValue >
            dimensionLimits[
              field
            ]
          ) {
            if (
              field ===
              "length"
            ) {
              return isSpecialLoad
                ? t(
                    "errors.specialLengthMax"
                  )
                : t(
                    "errors.lengthMax"
                  );
            }

            if (
              field ===
              "width"
            ) {
              return isSpecialLoad
                ? t(
                    "errors.specialWidthMax"
                  )
                : t(
                    "errors.widthMax"
                  );
            }

            return isSpecialLoad
              ? t(
                  "errors.specialHeightMax"
                )
              : t(
                  "errors.heightMax"
                );
          }

          return "";
        };

      const locationError =
        !String(
          newStopDraft.location ||
          ""
        ).trim();

      const dateError =
        !String(
          newStopDraft.date ||
          ""
        ).trim();

      const hourError =
        !/^([01]\d|2[0-3]):([0-5]\d)$/.test(
          String(
            newStopDraft.hour ||
            ""
          ).trim()
        );

      const lengthError =
        getDimensionError(
          "length",
          newStopDraft.length
        );

      const widthError =
        getDimensionError(
          "width",
          newStopDraft.width
        );

      const heightError =
        getDimensionError(
          "height",
          newStopDraft.height
        );

      const handlingEquipmentOtherError =
        String(
          newStopDraft.handlingEquipment ||
          ""
        )
          .trim()
          .toUpperCase() ===
          "OTHER" &&
        !String(
          newStopDraft.handlingEquipmentOther ||
          ""
        ).trim();

      const validatedDraft = {
        ...newStopDraft,

        type:
          newStopType,

        locationError,
        dateError,
        hourError,
        lengthError,
        widthError,
        heightError,
        handlingEquipmentOtherError,
      };

      setNewStopDraft(
        validatedDraft
      );

      if (
        locationError ||
        dateError ||
        hourError ||
        lengthError ||
        widthError ||
        heightError ||
        handlingEquipmentOtherError
      ) {
        return;
      }

      if (
        newStopType ===
        "PICKUP"
      ) {
        if (
          !canAddPickup
        ) {
          return;
        }

        const added =
          onAddPickup?.(
            validatedDraft
          );

        if (
          added ===
          false
        ) {
          return;
        }
      } else {
        if (
          !canAddDelivery
        ) {
          return;
        }

        const added =
          onAddDelivery?.(
            validatedDraft
          );

        if (
          added ===
          false
        ) {
          return;
        }
      }

      closeNewStopModal();
    };

  return (
    <section className="shipment-v2-panel shipment-v2-route-panel pendent-workspace-route-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {t(
            "routePanel.title"
          )}
        </h2>

        {!readOnly && (
          <span
            className="shipment-v2-plain-action pendent-workspace-reorder-label"
            title={
              t(
                "routePanel.reorderUnavailable"
              )
            }
          >
            {t(
              "routePanel.reorderStops"
            )}
          </span>
        )}
      </div>

      <div className="shipment-v2-stops-list">
        {routeStops.map(
          (
            stop
          ) => {
            const isExpanded =
              expandedStopKey ===
              stop.__renderKey;

            const isEditing =
              editingStopKey ===
              stop.__renderKey;

            const displayStop =
              isEditing &&
              draft
                ? draft
                : stop;

            const stateClass =
              stop.__isPickup
                ? "pending"
                : "delivery";

            const detailFields = [
              {
                label:
                  t(
                  "routePanel.fields.location"
                ),

                field:
                  "location",

                value:
                  displayStop.location ||
                  displayStop.placeName,
              },

              {
                label:
                  t(
                  "routePanel.fields.date"
                ),

                field:
                  "date",

                value:
                  displayStop.date,

                type:
                  "date",
              },

              {
                label:
                  t(
                  "routePanel.fields.hour"
                ),

                field:
                  "hour",

                value:
                  displayStop.hour,

                type:
                  "time",
              },

              {
                label:
                  t(
                  "fields.reference"
                ),

                field:
                  "reference",

                value:
                  displayStop.reference,
              },

              {
                label:
                  t(
                  "placeholders.address"
                ),

                field:
                  "address",

                value:
                  displayStop.address,
              },

              {
                label:
                  t(
                  "placeholders.postalCode"
                ),

                field:
                  "postalCode",

                value:
                  displayStop.postalCode,
              },

              {
                label:
                  t(
                  "placeholders.city"
                ),

                field:
                  "city",

                value:
                  displayStop.city,
              },

              {
                label:
                  t(
                  "placeholders.country"
                ),

                field:
                  "country",

                value:
                  displayStop.country,
              },

              {
                label:
                  t(
                  "routePanel.fields.phone"
                ),

                field:
                  "contactPhone",

                value:
                  displayStop.contactPhone,

                type:
                  "tel",
              },

              {
                label:
                  t(
                  "fields.pointLengthShort"
                ),

                field:
                  "length",

                value:
                  displayStop.length,

                suffix:
                  ` ${t(
                    "units.meterShort"
                  )}`,

                type:
                  "number",

                step:
                  "0.01",

                inputMode:
                  "decimal",
              },

              {
                label:
                  t(
                  "fields.pointWidthShort"
                ),

                field:
                  "width",

                value:
                  displayStop.width,

                suffix:
                  ` ${t(
                    "units.meterShort"
                  )}`,

                type:
                  "number",

                step:
                  "0.01",

                inputMode:
                  "decimal",
              },

              {
                label:
                  t(
                  "fields.pointHeightShort"
                ),

                field:
                  "height",

                value:
                  displayStop.height,

                suffix:
                  ` ${t(
                    "units.meterShort"
                  )}`,

                type:
                  "number",

                step:
                  "0.01",

                inputMode:
                  "decimal",
              },

              {
                label:
                  t(
                  "loadingAccess.label"
                ),

                field:
                  "loadingAccess",

                value:
                  displayStop.loadingAccess,

                displayValue:
                  getTranslatedEnumLabel(
                    displayStop.loadingAccess,
                    LOADING_ACCESS_I18N_KEYS,
                    "loadingAccess.options",
                    t
                  ),
              },

              {
                label:
                  t(
                  "handlingEquipment.label"
                ),

                field:
                  "handlingEquipment",

                value:
                  displayStop.handlingEquipment,

                displayValue:
                  getTranslatedEnumLabel(
                    displayStop.handlingEquipment,
                    HANDLING_EQUIPMENT_I18N_KEYS,
                    "handlingEquipment.options",
                    t
                  ),
              },

              {
                label:
                  t(
                  "routePanel.fields.googleMaps"
                ),

                field:
                  "gMapsLink",

                value:
                  displayStop.gMapsLink,
              },

              {
                label:
                  t(
                  "placeholders.coordinates"
                ),

                field:
                  "coordinates",

                value:
                  displayStop.coordinates,
              },
            ];

            return (
              <div
                key={
                  stop.__renderKey
                }
                className="shipment-v2-stop-row"
              >
                {readOnly ? (
                  <span
                    className="shipment-v2-drag-btn pendent-workspace-drag-placeholder"
                    aria-hidden="true"
                  />
                ) : (
                  <button
                    type="button"
                    className="shipment-v2-drag-btn"
                    aria-label={
                      t(
                        "routePanel.reorderStopAria",
                        {
                          stop:
                            getStopTitle(
                              stop,
                              t(
                                "routePanel.locationUndefined"
                              )
                            ),
                        }
                      )
                    }
                    title={
                      t(
                        "routePanel.reorderUnavailable"
                      )
                    }
                  >
                    <GripVertical
                      size={
                        18
                      }
                    />
                  </button>
                )}

                <article
                  className={[
                    "shipment-v2-stop-card",
                    stateClass,
                    "edit-request-stop-card",

                    isExpanded
                      ? "is-expanded"
                      : "",
                  ]
                    .filter(
                      Boolean
                    )
                    .join(
                      " "
                    )}
                >
                  <div className="edit-request-stop-summary">
                    <div className="shipment-v2-stop-timeline">
                      <span>
                        {stop.__stopNumber}
                      </span>
                    </div>

                    <div className="shipment-v2-stop-content">
                      <div className="shipment-v2-stop-top">
                        <span
                          className={`shipment-v2-stop-type ${stateClass}`}
                        >
                          {stop.__isPickup
                            ? t(
                                "routePanel.stopType.pickup"
                              )
                            : t(
                                "routePanel.stopType.delivery"
                              )}
                        </span>

                        <span
                          className={`shipment-v2-stop-status ${stateClass}`}
                        >
                          {displayStop.location
                            ? t(
                                "common.configured"
                              )
                            : t(
                                "common.undefined"
                              )}
                        </span>
                      </div>

                      <h3>
                        {getStopTitle(
                          displayStop,
                          t(
                            "routePanel.locationUndefined"
                          )
                        )}
                      </h3>

                      <p>
                        {getStopLocation(
                          displayStop,
                          t(
                            "routePanel.locationDescriptionUndefined"
                          )
                        )}
                      </p>

                      <div className="shipment-v2-stop-date">
                        <CalendarDays
                          size={
                            14
                          }
                        />

                        {getStopDateLabel(
                          displayStop,
                          locale,
                          t
                        )}
                      </div>
                    </div>

                    <div className="edit-request-stop-summary-actions">
                      <div className="shipment-v2-stop-side-icon">
                        {stop.__isPickup ? (
                          <CheckCircle2
                            size={
                              18
                            }
                          />
                        ) : (
                          <Clock3
                            size={
                              18
                            }
                          />
                        )}
                      </div>

                      <button
                        type="button"
                        className={[
                          "edit-request-stop-expand-btn",

                          isExpanded
                            ? "is-open"
                            : "",
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " "
                          )}
                        aria-expanded={
                          isExpanded
                        }
                        aria-label={
                          isExpanded
                            ? t(
                                "routePanel.closeDetails"
                              )
                            : t(
                                "routePanel.openDetails"
                              )
                        }
                        onClick={() =>
                          setExpandedStopKey(
                            (
                              currentKey
                            ) =>
                              currentKey ===
                              stop.__renderKey
                                ? ""
                                : stop.__renderKey
                          )
                        }
                      >
                        <ChevronDown
                          size={
                            19
                          }
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    className={[
                      "edit-request-stop-details-wrapper",

                      isExpanded
                        ? "is-open"
                        : "",
                    ]
                      .filter(
                        Boolean
                      )
                      .join(
                        " "
                      )}
                  >
                    <div className="edit-request-stop-details-inner">
                      <div className="edit-request-stop-details-panel">
                        <div className="edit-request-stop-details-grid">
                          {detailFields.map(
                            (
                              field
                            ) => (
                              <StopDetailItem
                                key={
                                  field.field
                                }
                                {...field}
                                isEditing={
                                  isEditing
                                }
                                onChange={
                                  updateDraft
                                }
                                undefinedLabel={
                                  t(
                                    "common.undefined"
                                  )
                                }
                              />
                            )
                          )}
                        </div>

                        <div className="edit-request-stop-footer">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="edit-request-stop-cancel-btn"
                                onClick={
                                  cancelEditing
                                }
                              >
                                {t(
                                  "buttons.cancel"
                                )}
                              </button>

                              <button
                                type="button"
                                className="edit-request-stop-save-btn"
                                onClick={() =>
                                  saveStop(
                                    stop
                                  )
                                }
                              >
                                <CheckCircle2
                                  size={
                                    15
                                  }
                                />

                                {t(

                                  "routePanel.saveStop"

                                )}
                              </button>
                            </>
                          ) : (
                            !readOnly && (
                              <div className="pendent-workspace-stop-footer-actions">
                                <button
                                  type="button"
                                  className="pendent-workspace-remove-stop-btn"
                                  onClick={() =>
                                    removeStop(
                                      stop
                                    )
                                  }
                                >
                                  <X
                                    size={
                                      15
                                    }
                                  />

                                  {t(

                                    "routePanel.remove"

                                  )}
                                </button>

                                <button
                                  type="button"
                                  className="edit-request-stop-edit-btn"
                                  onClick={() =>
                                    startEditing(
                                      stop
                                    )
                                  }
                                >
                                  <Pencil
                                    size={
                                      15
                                    }
                                  />

                                  {t(

                                    "routePanel.editDetails"

                                  )}
                                </button>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            );
          }
        )}
      </div>

      {!readOnly && (
        <div className="pendent-workspace-add-stop-wrap">
          <button
            type="button"
            className="shipment-v2-add-stop-btn"
            onClick={() =>
              setAddMenuOpen(
                (
                  currentValue
                ) =>
                  !currentValue
              )
            }
          >
            <Plus
              size={
                18
              }
            />

            {t(

              "routePanel.addStop"

            )}
          </button>

          {addMenuOpen && (
            <div className="pendent-workspace-add-stop-menu">
              <button
                type="button"
                disabled={
                  !canAddPickup
                }
                onClick={() =>
                  openNewStopModal(
                    "PICKUP"
                  )
                }
              >
                {t(
                  "routePanel.addPickup"
                )}
              </button>

              <button
                type="button"
                disabled={
                  !canAddDelivery
                }
                onClick={() =>
                  openNewStopModal(
                    "DELIVERY"
                  )
                }
              >
                {t(
                  "routePanel.addDelivery"
                )}
              </button>
            </div>
          )}
        </div>
      )}

<div className="shipment-v2-route-note">
        <Info
          size={
            15
          }
        />

        {readOnly
          ? t(
              "routePanel.readOnlyNote"
            )
          : t(
              "routePanel.editableNote"
            )}
      </div>

      {newStopDraft && (
        <div
          className="current-load-new-stop-modal-overlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeNewStopModal();
            }
          }}
        >
          <div
            className="current-load-new-stop-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pendent-load-new-stop-modal-title"
          >
            <div className="current-load-new-stop-modal__header">
              <div>
                <span
                  className={`current-load-new-stop-modal__type ${
                    newStopType ===
                    "PICKUP"
                      ? "is-pickup"
                      : "is-delivery"
                  }`}
                >
                  {newStopType ===
                  "PICKUP"
                    ? t(
                        "routePanel.stopType.pickup"
                      )
                    : t(
                        "routePanel.stopType.delivery"
                      )}
                </span>

                <h2 id="pendent-load-new-stop-modal-title">
                  {newStopType ===
                  "PICKUP"
                    ? t(
                        "routePanel.newPickupTitle",
                        {
                          defaultValue:
                            "Novo ponto de carga",
                        }
                      )
                    : t(
                        "routePanel.newDeliveryTitle",
                        {
                          defaultValue:
                            "Novo ponto de descarga",
                        }
                      )}
                </h2>

                <p>
                  {t(
                    "routePanel.newStopHelper",
                    {
                      defaultValue:
                        "Preenche os dados do novo ponto antes de o adicionar à rota.",
                    }
                  )}
                </p>
              </div>

              <button
                type="button"
                className="current-load-new-stop-modal__close"
                aria-label={
                  t(
                    "buttons.cancel"
                  )
                }
                onClick={
                  closeNewStopModal
                }
              >
                <X
                  size={
                    18
                  }
                />
              </button>
            </div>

            <div className="current-load-new-stop-modal__body">
              <div className="current-load-new-stop-modal__grid">
                <label className="current-load-new-stop-modal__field current-load-new-stop-modal__field--wide">
                  <span>
                    {t(
                      "routePanel.fields.location"
                    )}
                  </span>

                  <CreatableSelect
                    isClearable
                    isMulti={
                      false
                    }
                    options={
                      placeOptions
                    }
                    placeholder={
                      t(
                        "place.selectOrType",
                        {
                          defaultValue:
                            "Selecionar ou escrever um local",
                        }
                      )
                    }
                    value={
                      resolvePlaceSelectValue(
                        newStopDraft,
                        placeOptions
                      )
                    }
                    formatOptionLabel={
                      renderPlaceOption
                    }
                    formatCreateLabel={(
                      value
                    ) =>
                      t(
                        "place.createOption",
                        {
                          value,

                          defaultValue:
                            `Usar "${value}"`,
                        }
                      )
                    }
                    onChange={
                      handleNewStopPlaceChange
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={
                      safeDocumentBody()
                    }
                    menuPosition="fixed"
                  />

                  {newStopDraft.locationError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.locationRequired",
                        {
                          defaultValue:
                            "O local é obrigatório.",
                        }
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "routePanel.fields.date"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__input-with-icon">
                    <input
                      ref={
                        newStopDateInputRef
                      }
                      type="date"
                      value={
                        newStopDraft.date ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          date:
                            event.target.value,

                          dateError:
                            false,
                        })
                      }
                    />

                    <button
                      type="button"
                      className="current-load-new-stop-modal__field-icon-btn"
                      aria-label={
                        t(
                          "routePanel.fields.date"
                        )
                      }
                      onClick={() => {
                        if (
                          newStopDateInputRef.current
                            ?.showPicker
                        ) {
                          newStopDateInputRef.current.showPicker();

                          return;
                        }

                        newStopDateInputRef.current?.focus();
                      }}
                    >
                      <CalendarDays
                        size={
                          16
                        }
                      />
                    </button>
                  </div>

                  {newStopDraft.dateError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.dateRequired",
                        {
                          defaultValue:
                            "A data é obrigatória.",
                        }
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "routePanel.fields.hour"
                    )}
                  </span>

                  <input
                    type="time"
                    value={
                      newStopDraft.hour ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        hour:
                          event.target.value,

                        hourError:
                          false,
                      })
                    }
                  />

                  {newStopDraft.hourError && (
                    <small className="current-load-new-stop-modal__error">
                      {t(
                        "errors.hourInvalid"
                      )}
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.reference"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.reference ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        reference:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field current-load-new-stop-modal__field--wide">
                  <span>
                    {t(
                      "placeholders.address"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.address ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        address:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.postalCode"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.postalCode ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        postalCode:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.city"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.city ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        city:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "placeholders.country"
                    )}
                  </span>

                  <input
                    type="text"
                    value={
                      newStopDraft.country ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        country:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "routePanel.fields.phone"
                    )}
                  </span>

                  <input
                    type="tel"
                    value={
                      newStopDraft.contactPhone ||
                      ""
                    }
                    onChange={(event) =>
                      patchNewStop({
                        contactPhone:
                          event.target.value,
                      })
                    }
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointLengthShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.length ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          length:
                            event.target.value,

                          lengthError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.lengthError && (
                    <small className="current-load-new-stop-modal__error">
                      {
                        newStopDraft.lengthError
                      }
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointWidthShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.width ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          width:
                            event.target.value,

                          widthError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.widthError && (
                    <small className="current-load-new-stop-modal__error">
                      {
                        newStopDraft.widthError
                      }
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "fields.pointHeightShort"
                    )}
                  </span>

                  <div className="current-load-new-stop-modal__dimension">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        newStopDraft.height ??
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          height:
                            event.target.value,

                          heightError:
                            "",
                        })
                      }
                    />

                    <small>
                      {t(
                        "units.meterShort"
                      )}
                    </small>
                  </div>

                  {newStopDraft.heightError && (
                    <small className="current-load-new-stop-modal__error">
                      {
                        newStopDraft.heightError
                      }
                    </small>
                  )}
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "loadingAccess.label"
                    )}
                  </span>

                  <Select
                    isClearable
                    options={
                      loadingAccessOptions
                    }
                    value={
                      loadingAccessOptions.find(
                        (
                          option
                        ) =>
                          option.value ===
                          newStopDraft.loadingAccess
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      patchNewStop({
                        loadingAccess:
                          option?.value ||
                          null,
                      })
                    }
                    placeholder={
                      t(
                        "loadingAccess.placeholder"
                      )
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={
                      safeDocumentBody()
                    }
                    menuPosition="fixed"
                  />
                </label>

                <label className="current-load-new-stop-modal__field">
                  <span>
                    {t(
                      "handlingEquipment.label"
                    )}
                  </span>

                  <Select
                    isClearable
                    options={
                      handlingEquipmentOptions
                    }
                    value={
                      handlingEquipmentOptions.find(
                        (
                          option
                        ) =>
                          option.value ===
                          newStopDraft.handlingEquipment
                      ) ||
                      null
                    }
                    onChange={(option) =>
                      patchNewStop({
                        handlingEquipment:
                          option?.value ||
                          null,

                        handlingEquipmentOther:
                          option?.value ===
                          "OTHER"
                            ? newStopDraft.handlingEquipmentOther ||
                              ""
                            : "",

                        handlingEquipmentOtherError:
                          false,
                      })
                    }
                    placeholder={
                      t(
                        "handlingEquipment.placeholder"
                      )
                    }
                    noOptionsMessage={() =>
                      t(
                        "common.noOptions"
                      )
                    }
                    classNamePrefix="current-load-workspace-select"
                    menuPortalTarget={
                      safeDocumentBody()
                    }
                    menuPosition="fixed"
                  />
                </label>

                {String(
                  newStopDraft.handlingEquipment ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                  "OTHER" && (
                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "handlingEquipment.otherLabel"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.handlingEquipmentOther ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          handlingEquipmentOther:
                            event.target.value,

                          handlingEquipmentOtherError:
                            false,
                        })
                      }
                    />

                    {newStopDraft.handlingEquipmentOtherError && (
                      <small className="current-load-new-stop-modal__error">
                        {t(
                          "errors.handlingEquipmentOtherRequired",
                          {
                            defaultValue:
                              "Indica o outro equipamento.",
                          }
                        )}
                      </small>
                    )}
                  </label>
                )}

                <div className="current-load-new-stop-modal__pair-row">
                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "routePanel.fields.googleMaps"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.gMapsLink ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          gMapsLink:
                            event.target.value,
                        })
                      }
                    />
                  </label>

                  <label className="current-load-new-stop-modal__field">
                    <span>
                      {t(
                        "placeholders.coordinates"
                      )}
                    </span>

                    <input
                      type="text"
                      value={
                        newStopDraft.coordinates ||
                        ""
                      }
                      onChange={(event) =>
                        patchNewStop({
                          coordinates:
                            event.target.value,
                        })
                      }
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="current-load-new-stop-modal__footer">
              <button
                type="button"
                className="current-load-new-stop-modal__cancel"
                onClick={
                  closeNewStopModal
                }
              >
                {t(
                  "buttons.cancel"
                )}
              </button>

              <button
                type="button"
                className="current-load-new-stop-modal__confirm"
                onClick={
                  confirmNewStop
                }
              >
                {newStopType ===
                "PICKUP"
                  ? t(
                      "routePanel.addPickup"
                    )
                  : t(
                      "routePanel.addDelivery"
                    )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}